import { useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

export default function AdminEpisodes({ episodes, shows, refresh }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showFilter, setShowFilter] = useState("all");
  const [langFilter, setLangFilter] = useState("all");

  const filtered = useMemo(() => {
    return episodes.filter((ep) =>
      (showFilter === "all" || ep.show_slug === showFilter) &&
      (langFilter === "all" || ep.language === langFilter)
    );
  }, [episodes, showFilter, langFilter]);

  const del = async (ep) => {
    if (!confirm(`Delete episode "${ep.title}"?`)) return;
    try {
      await api.delete(`/admin/episodes/${ep.id}`);
      toast.success("Episode deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleFeatured = async (ep) => {
    try {
      await api.put(`/admin/episodes/${ep.id}`, { ...ep, featured: !ep.featured });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-episodes-section">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-4">
        <div className="flex gap-3">
          <select value={showFilter} onChange={(e) => setShowFilter(e.target.value)} data-testid="episode-filter-show" className="bg-black border border-white/15 px-3 py-2 text-white text-sm">
            <option value="all">All shows</option>
            {shows.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
          </select>
          <select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} data-testid="episode-filter-language" className="bg-black border border-white/15 px-3 py-2 text-white text-sm">
            <option value="all">All languages</option>
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <span className="text-sm text-neutral-400 self-center">{filtered.length} episodes</span>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={() => { setEditing(null); setOpen(true); }} className="bg-svn-red text-white font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-red-700 inline-flex items-center gap-2" data-testid="admin-new-episode-btn">
              <Plus size={14} /> New Episode
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editing ? "Edit Episode" : "New Episode"}</DialogTitle>
            </DialogHeader>
            <EpisodeForm episode={editing} shows={shows} onSaved={() => { setOpen(false); refresh(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-[#0F0F0F] border border-white/10 overflow-x-auto" data-testid="admin-episodes-table">
        <table className="w-full text-sm">
          <thead className="bg-black/60">
            <tr>
              {["", "Title", "Show", "Lang", "YouTube", "Duration", ""].map((h, i) => (
                <th key={i} className="text-left text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-400 px-3 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ep) => (
              <tr key={ep.id} className="border-t border-white/5">
                <td className="px-3 py-3">
                  <button onClick={() => toggleFeatured(ep)} title={ep.featured ? "Unfeature" : "Mark as featured"} data-testid={`toggle-featured-${ep.id}`}>
                    <Star size={16} className={ep.featured ? "text-gold fill-gold" : "text-neutral-600 hover:text-gold"} />
                  </button>
                </td>
                <td className="px-3 py-3 text-white max-w-[260px] truncate">{ep.title}</td>
                <td className="px-3 py-3 text-neutral-300 text-xs">{ep.show_slug}</td>
                <td className="px-3 py-3 text-neutral-400 text-xs uppercase">{ep.language || "en"}</td>
                <td className="px-3 py-3 text-neutral-400 font-mono text-xs">{ep.youtube_id}</td>
                <td className="px-3 py-3 text-neutral-400">{ep.duration}</td>
                <td className="px-3 py-3 text-right">
                  <button onClick={() => { setEditing(ep); setOpen(true); }} className="text-neutral-400 hover:text-gold mr-3" data-testid={`edit-episode-${ep.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(ep)} className="text-neutral-400 hover:text-svn-red" data-testid={`delete-episode-${ep.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EpisodeForm({ episode, shows, onSaved }) {
  const [f, setF] = useState({
    show_slug: episode?.show_slug || shows[0]?.slug || "",
    title: episode?.title || "",
    description: episode?.description || "",
    youtube_id: episode?.youtube_id || "",
    thumbnail_url: episode?.thumbnail_url || "",
    duration: episode?.duration || "",
    topics: (episode?.topics || []).join(", "),
    featured: episode?.featured || false,
    language: episode?.language || "en",
    published_at: episode?.published_at || "",
    youtube_url: episode?.youtube_url || "",
    spotify_url: episode?.spotify_url || "",
    facebook_url: episode?.facebook_url || "",
  });
  const [saving, setSaving] = useState(false);

  // Auto-set language from show
  const onShowChange = (slug) => {
    const show = shows.find((s) => s.slug === slug);
    setF({ ...f, show_slug: slug, language: show?.language || f.language });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...f,
      topics: f.topics.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (episode) await api.put(`/admin/episodes/${episode.id}`, payload);
      else await api.post("/admin/episodes", payload);
      toast.success("Episode saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const I = (k, label) => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`episode-form-${k}`} className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-3" data-testid="episode-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Show</label>
          <select value={f.show_slug} onChange={(e) => onShowChange(e.target.value)} data-testid="episode-form-show_slug" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold">
            {shows.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Language</label>
          <select value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} data-testid="episode-form-language" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
      {I("title", "Title")}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Description</label>
        <textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="episode-form-description" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {I("youtube_id", "YouTube video ID")}
        {I("thumbnail_url", "Thumbnail URL (optional)")}
        {I("duration", "Duration (e.g. 48:21)")}
        {I("topics", "Topics (comma)")}
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {I("youtube_url", "YouTube link")}
        {I("spotify_url", "Spotify link")}
        {I("facebook_url", "Facebook link")}
      </div>
      {I("published_at", "Published at (ISO date — optional)")}
      <label className="flex items-center gap-3 text-sm text-neutral-300">
        <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} data-testid="episode-form-featured" className="accent-yellow-400" />
        Featured on the show page
      </label>
      <button type="submit" disabled={saving} data-testid="episode-form-save" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-6 py-3 hover:bg-red-700 disabled:opacity-60">
        {saving ? "Saving…" : "Save Episode"}
      </button>
    </form>
  );
}
